/**
 * useOAuth
 * This composable should contain and expose helper functions related to OAuth provider ONLY.
 * Generic authentication methods or states that can be applied to other auth schemes or providers must not be placed here.
 */

import queryString from 'query-string'
import type { UserTokenData } from '~/types/auth'

type OAuthOperation = 'authorize' | 'check' | 'logout'

interface UseOAuthReturn {
  buildBaseUrl: (operation: OAuthOperation) => string
  buildRedirectUrl: (operation: OAuthOperation) => string
  parseCallbackHash: (hash: string) => UserTokenData
}

export const useOAuth = (): UseOAuthReturn => {
  const buildBaseUrl = (operation: OAuthOperation): string => {
    const config = useRuntimeConfig().public
    const serverUrl = config.oauthServerUrl

    switch (operation) {
      case 'authorize':
        return serverUrl + config.oauthAuthorizeRoute
      case 'check':
        return serverUrl + config.oauthCheckRoute
      case 'logout':
        return serverUrl + config.oauthLogoutRoute
      default:
        return ''
    }
  }

  const buildRedirectUrl = (operation: OAuthOperation): string => {
    const config = useRuntimeConfig().public
    const baseUrl = buildBaseUrl(operation)
    const callbackUrl = `${config.appUrl}/oauth/callback`

    switch (operation) {
      case 'authorize':
        return `${baseUrl}?client_id=${config.oauthClientId}&redirect_uri=${callbackUrl}&response_type=${config.oauthResponseType}&scope=${config.oauthScope}`
      case 'check':
        return baseUrl
      case 'logout':
        return `${baseUrl}?intended=${callbackUrl}`
      default:
        return ''
    }
  }

  const parseCallbackHash = (hash: string): UserTokenData => {
    const { access_token, expires_in, token_type } = queryString.parse(hash)

    if (!access_token || !expires_in || !token_type) {
      throw new Error('Invalid OAuth Hash')
    }

    return {
      access_token: String(access_token),
      expires_in: Number(expires_in),
      token_type: String(token_type)
    }
  }

  return {
    buildBaseUrl,
    buildRedirectUrl,
    parseCallbackHash
  }
}
